import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ApiAuthBridge } from "@/components/ApiAuthBridge";
import { SignupConversion } from "@/components/SignupConversion";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { ProjectProvider } from "@/contexts/ProjectContext";
import { EnvironmentProvider } from "@/contexts/EnvironmentContext";
import { ProjectEnvironmentProvider } from "@/contexts/ProjectEnvironmentContext";
import { CloudProvider } from "@/contexts/CloudContext";
import { PlanProvider } from "@/contexts/PlanContext";
import { seedMockData, reseedMockData, clearMockData } from "@/utils/seedMockData";

// Seed mock data ONLY in explicit demo mode (VITE_DEMO_MODE=true). Previously this
// also seeded whenever mock-fallback wasn't explicitly disabled, which is the default
// — so localStorage got pre-loaded with a fake "GizmoData Demo" org + clusters that
// masked the real API data (you'd land on the demo org and real entities wouldn't
// show). Mock-fallback (returning mock data on an API *failure*) is a separate
// resilience concern and no longer implies seeding primary data.
const SEED_MOCK_DATA = import.meta.env.VITE_DEMO_MODE === 'true';
if (SEED_MOCK_DATA) {
  seedMockData();
}

// Expose mock data utilities on window for debugging
if (typeof window !== 'undefined') {
  Object.assign(window, {
    mockData: { reseed: reseedMockData, clear: clearMockData, seed: seedMockData },
  });
}
import { SignInLanding } from "@/pages/SignInLanding";
import { PageLoading } from "@/components/PageLoading";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { lazyPage } from "@/lib/lazyPage";

// Everything behind sign-in loads on demand (see AuthenticatedApp.tsx).
const AuthenticatedApp = lazyPage(() => import("./AuthenticatedApp"), "default");
import { RequireSignedIn } from "@/components/RequireSignedIn";
// A Hardcharger web UI's "Sign in with GizmoData Cloud" lands here (the API's authorization_endpoint): signed in,
// but standalone like /sign-in (no Layout), since it is one step of the web UI's sign-in, not a page to browse.
const WebUiSignIn = lazyPage(() => import("@/pages/WebUiSignIn"), "WebUiSignIn");
import { ClarityRouteGuard } from '@/components/ClarityRouteGuard';

const queryClient = new QueryClient();

const App = () => (
  <AppErrorBoundary scope="app">
  <QueryClientProvider client={queryClient}>
    {/* Mount ApiAuthBridge first so apiClient has Clerk's getToken wired in
        before any provider below issues its first authenticated request. */}
    <ApiAuthBridge />
    <SignupConversion />
    <ThemeProvider>
      <CloudProvider>
        <OrganizationProvider>
          <PlanProvider>
            <ProjectProvider>
              <EnvironmentProvider>
                <ProjectEnvironmentProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <BrowserRouter>
                    <ClarityRouteGuard />
                    {/* `/sign-in` is the only route that renders bare (no
                        Layout, no first-time-org modals). Everything else
                        sits inside `<RequireSignedIn>` so unauthenticated
                        users are bounced before any provider effects run,
                        page hooks mount, or auth-required API calls fire.
                        The previous per-page `<SignedOut>` blocks are now
                        unreachable for guarded routes — see the cleanup
                        sweep referenced in the sync backlog. */}
                    <Routes>
                      <Route path="/sign-in/*" element={<SignInLanding />} />
                      <Route path="/sign-up/*" element={<SignInLanding mode="sign-up" />} />
                      <Route
                        path="/web-ui-sign-in/:clusterId"
                        element={
                          <RequireSignedIn>
                            <Suspense fallback={<PageLoading fullScreen />}>
                              <WebUiSignIn />
                            </Suspense>
                          </RequireSignedIn>
                        }
                      />
                      <Route
                        path="*"
                        element={
                          <RequireSignedIn>
                            <Suspense fallback={<PageLoading fullScreen />}>
                              <AuthenticatedApp />
                            </Suspense>
                          </RequireSignedIn>
                        }
                      />
                    </Routes>
                  </BrowserRouter>
                </TooltipProvider>
                </ProjectEnvironmentProvider>
              </EnvironmentProvider>
            </ProjectProvider>
          </PlanProvider>
        </OrganizationProvider>
      </CloudProvider>
    </ThemeProvider>
  </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
